<template>
    <div v-if="visible" class="container">
        <div class="row no-gutters">
            <div class="col-sm">
                <div class="color-legend">
                    <div class="staff-gone">Po pracy</div>
                    <div class="staff-coming">Przed pracą</div>
                    <div class="staff-late">Spóźnienie</div>
                    <div class="staff-present">Obecny</div>
                </div>
            </div>
        </div>
        <form @submit.prevent="showEmployees">
            <div class="row">
                <div class="col-md-4">
                    <div class="input-group mb-3">
                        <div class="input-group-prepend">
                            <vs-tooltip text="Data">
                                <span class="input-group-text"><i class="material-icons">today</i></span>
                            </vs-tooltip>
                        </div>
                        <input class="form-control" type="date" id="form-date" v-model="date"
                                max="9999-12-31" required/>
                    </div>
                </div>
            </div>
        </form>
        <div class="row">
            <div class="col-md">
                <div class="box-bordered p-l-10 p-r-10 p-b-10">
                    <table class="table table-hover table-staff table-responsive-md">
                        <thead>
                            <tr>
                                <th scope="col">Login</th>
                                <th scope="col">Dział</th>
                                <th scope="col">Godzina od</th>
                                <th scope="col">Godzina do</th>
                                <th scope="col">Miejsce</th>
                                <th scope="col">Projekt</th>
                                <th scope="col">Urlop</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(employees, key) in employeesFiltered">
                                <tr :class="`staff-${key}`" v-for="employee in employees">
                                    <td class="clickable" @click="employeeDetails(employee.id)">{{ employee.login }}</td>
                                    <td>{{ formatArea(employee.area) }}</td>
                                    <td>{{ formatTime(employee.from_hour) }}</td>
                                    <td>{{ formatTime(employee.to_hour) }}</td>
                                    <td>{{ employee.place }}</td>
                                    <td>{{ formatProject(employee.project) }}</td>
                                    <td>{{ employee.absence.length > 0 ? "U" : "" }}</td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {backend} from "../../backend.js";
    import Vue from 'vue';
    import Vuesax from 'vuesax';
    import moment from 'moment';

    Vue.use(Vuesax);

    moment.locale('pl');

    export default {
        name: "EmployeeList",
        props: {
            areas: [],
            projects: []
        },
        data: function () {
            return {
                queryParams: {},
                employees: [],
                employeesFiltered: {
                    present: [],
                    late: [],
                    coming: [],
                    gone: [],
                },
                interval: null,
                visible: false,
                date: moment().format('YYYY-MM-DD'),
            }
        },
        created() {
            this.showEmployees().then(() => this.visible = true);
            this.interval = setInterval(function () {
                this.showEmployees();
            }.bind(this), 5000);
        },
        watch: {
            date() {
                this.queryParams.date = this.date;
                this.showEmployees();
            },
        },
        beforeDestroy() {
            clearInterval(this.interval);
        },
        methods: {
            employeeDetails: function(pk) {
                const employee = this.employees.find(employee => employee.id === pk);
                if (!employee) return;
                return backend.getEmployees().then(({ data }) => {
                    const user = data.find(info => info.remote_user === pk);
                    const supervisor = data.find(info => info.id === user.supervisor);
                    const details = {
                        login: employee.login,
                        name: employee.name,
                        surname: employee.surname,
                        supervisor: supervisor ? supervisor.username : "Brak",
                        area: employee.area,
                        date_joined: this.removeTimeFromDate(user.date_joined),
                        id: employee.id,
                    }
                    this.$emit('changeDetail', {component_change: 'employee-details', details});
                });
            },
            clearData: function() {
                this.employeesFiltered = {
                    present: [],
                    late: [],
                    coming: [],
                    gone: []
                }
                this.employees = [];
            },
            showEmployees: function() {
                return backend.getRemoteUsers(this.queryParams)
                    .then(response => {
                        let act_date = new Date();
                        let selected_date = new Date(this.date);
                        let today = new Date(moment().format('YYYY-MM-DD'));
                        let is_past = selected_date < today;
                        let is_future = selected_date > today;
                        this.clearData();
                        this.employees = response.data;
                        for (let employee of this.employees) {
                            for (let report of employee.report) {
                                let emp = JSON.parse(JSON.stringify(employee));
                                emp.place = report.place;
                                emp.from_hour = this.dateFromMinutes(report.from_hour, report.on_date);
                                if (report.to_hour !== -1) {
                                    emp.to_hour = this.dateFromMinutes(report.to_hour, report.on_date);
                                }
                                if (is_past || act_date > emp.to_hour) {
                                    this.employeesFiltered.gone.push(emp);
                                } else {
                                    this.employeesFiltered.present.push(emp);
                                }
                            }
                            for (let plan of employee.plan) {
                                let emp = JSON.parse(JSON.stringify(employee));
                                emp.place = plan.place;
                                emp.from_hour = this.dateFromMinutes(plan.from_hour);
                                emp.to_hour = this.dateFromMinutes(plan.to_hour);
                                if (is_future || (!is_past && act_date < emp.from_hour)) {
                                    this.employeesFiltered.coming.push(emp);
                                } else if (employee.report.findIndex(report => this.isOverlap(report, plan)) === -1) {
                                    this.employeesFiltered.late.push(emp);
                                }
                            }
                        }
                        let sortByFromHour = function(a, b) {
                            return a.from_hour - b.from_hour;
                        };
                        this.employeesFiltered.present.sort(sortByFromHour);
                        this.employeesFiltered.late.sort(sortByFromHour);
                        this.employeesFiltered.coming.sort(sortByFromHour);

                        let sortByToHour = function(a, b) {
                            return b.to_hour - a.to_hour;
                        };
                        this.employeesFiltered.gone.sort(sortByToHour);
                    })
                    .catch(() => {
                        this.$vs.notify({text: 'Nie udało się pobrać listy pracowników', color: 'danger'});
                    });
            },
            isOverlap (report, plan) {
                if (report.to_hour === -1) {
                    return report.from_hour <= plan.to_hour;
                }
                else {
                    return report.from_hour <= plan.to_hour && report.to_hour >= plan.from_hour;
                }
            },
            dateFromMinutes: function(minutes, day) {
                return moment(day).startOf('day').add(minutes, 'minutes');
            },
            formatTime: function (date) {
                return date ? moment(date).format('LT') : '';
            },
            removeTimeFromDate: function (date) {
                return date ? moment(date).format('DD/MM/YYYY') : '';
            },
            formatArea: function (areaId) {
                const area = this.areas.find(area => area.id === areaId);
                return area ? area.name : "";
            },
            formatProject: function (projectId) {
                const project = this.projects.find(proj => proj.id === projectId);
                return project ? project.name : "";
            }
        }
    }
</script>

<style lang="scss" scoped>
.table-staff {
    th {
        border-top:0;
        padding: 2px 5px;
    }
    tr td {
        border-top: 1px solid #b7b7b7;
        padding: 2px 5px;
    }
    .staff-present {
        background: rgba(232, 255, 223, 0.75);
        &:hover {
            background: darken( rgba(232, 255, 223, 0.75), 10% );
        }
    }
    .staff-coming {
        background: rgba(218, 255, 255, 0.52);
         &:hover {
            background: darken( rgba(218, 255, 255, 0.52), 10% );
        }
    }
    .staff-gone {
        background: #ddd;
         &:hover {
            background: darken( #ddd, 10% );
        }
    }
    .staff-late {
        background: rgba(249, 165, 165, 0.31);
         &:hover {
            background: darken( rgba(249, 165, 165, 0.31), 10% );
        }
    }
    .clickable {
      cursor: pointer;
      &:hover {
          text-decoration: underline;
      }
    }
}
 .color-legend {
        text-align: center;
        overflow: hidden;
        float: right;
        border: 1px solid #e7e7e7;
        margin: .2rem 0 1rem 0;
        border-radius: 2px;
        > div {
            float: right;
            padding: 2px 5px;
            font-size: .8rem;
        }
        .staff-present {
            background: rgba(232, 255, 223, 0.75);
        }
        .staff-coming {
            background: rgba(218, 255, 255, 0.52);
        }
        .staff-gone {
            background: #ddd;
        }
        .staff-late {
            background: rgba(249, 165, 165, 0.31);
        }
    }

    .employee-form-item {
        margin-right: 2em;
    }

</style>



// WEBPACK FOOTER //
// EmployeeList.vue?0964c9e3